import type { UserDetails } from "@/app/types/domain";

export const AUTH_STATE_CHANGED_EVENT = "auth-state-changed";

const USER_ID_KEY = "userId";
const USER_DETAILS_KEY = "userDetails";

function canUseStorage() {
  return typeof window !== "undefined";
}

export const authStorage = {
  getUserId() {
    if (!canUseStorage()) return null;
    return window.localStorage.getItem(USER_ID_KEY);
  },

  hasSession() {
    return Boolean(this.getUserId());
  },

  setUserId(userId: string) {
    if (!canUseStorage()) return;
    window.localStorage.setItem(USER_ID_KEY, userId);
  },

  clearUserId() {
    if (!canUseStorage()) return;
    window.localStorage.removeItem(USER_ID_KEY);
  },

  getUserDetails<T>() {
    if (!canUseStorage()) return null as T | null;

    const rawValue = window.localStorage.getItem(USER_DETAILS_KEY);
    if (!rawValue) return null as T | null;

    try {
      return JSON.parse(rawValue) as T;
    } catch {
      window.localStorage.removeItem(USER_DETAILS_KEY);
      return null as T | null;
    }
  },

  setUserDetails(userDetails: UserDetails) {
    if (!canUseStorage()) return;
    window.localStorage.setItem(USER_DETAILS_KEY, JSON.stringify(userDetails));
  },

  mergeUserDetails(userDetails: Partial<UserDetails>) {
    if (!canUseStorage()) return;

    const existingDetails = this.getUserDetails<UserDetails>() ?? {};
    window.localStorage.setItem(
      USER_DETAILS_KEY,
      JSON.stringify({
        ...existingDetails,
        ...userDetails,
      })
    );
  },

  clearUserDetails() {
    if (!canUseStorage()) return;
    window.localStorage.removeItem(USER_DETAILS_KEY);
  },

  clearSession() {
    if (!canUseStorage()) return;
    window.localStorage.removeItem(USER_ID_KEY);
    window.localStorage.removeItem(USER_DETAILS_KEY);
  },

  notifyAuthChanged() {
    if (!canUseStorage()) return;
    window.dispatchEvent(new Event(AUTH_STATE_CHANGED_EVENT));
  },
};
